import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactPhoneInput from 'react-phone-input-2';
import cn from 'classnames';
import isObject from 'lodash/isObject';
import { useFloating, autoUpdate, size } from '@floating-ui/react';
import startsWith from 'lodash/startsWith';
import isEqual from 'lodash/isEqual';
import isString from 'lodash/isString';
import { FormError } from '@creditflowresearch/uilib/Form/FormError';
import { FormField } from '@creditflowresearch/uilib/Form/FormField';
import { InputLabel as Label } from '@creditflowresearch/uilib/Input/Label';
import PortalWrp from '@creditflowresearch/uilib/Layout/PortalWrp';
import ClearButton from './ClearButton';

import inputStyles from '../input.module.scss';
import styles from './phoneInput.module.scss';

import { additionalCountries } from './data/additionalCountries';

// Workaround according to issue in v2.15.1 (React v18): https://github.com/bl00mber/react-phone-input-2/issues/533
// @ts-ignore
const PhoneInput = ReactPhoneInput.default ? ReactPhoneInput.default : ReactPhoneInput;

interface IProps {
  isFinalForm?: boolean,
  input: any,
  meta: any,
  name: string,
  label: string,
  placeholder: string,
  disabled?: boolean,
  isErrorMessage?: boolean,
  isNarrow?: boolean,
  defaultCountry?: string,
  prefix?: string,
  value: string | number,
  usePortal?: boolean,
  portalId: string,
  onChange?: (payload: any) => void,
  onBlur?: () => void,
  onFocus?: () => void,
  isClearable?: boolean,
  formFieldClassName: string,
  isSmall?: boolean,
  dataTest: string,
}

const Phone: React.FC<IProps> = (props) => {
  const {
    isFinalForm = false,
    disabled = false,
    isErrorMessage = true,
    isNarrow = false,
    isClearable = true,
    defaultCountry = 'us',
    prefix = '+',
    usePortal = false,
    isSmall = false,
    onChange = () => {},
    onBlur = () => {},
    onFocus = () => {},
    input,
    meta,
    label,
    placeholder,
    formFieldClassName,
    dataTest,
    portalId,
    value,
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [innerValue, setInnerValue] = useState('');
  const isChanged = useRef(false);
  const countryData = useRef(null);
  const defaultCountryData = useRef(null);

  const phoneInputRef = useRef(null);
  const nodeInput = useRef<HTMLInputElement>(null);

  const { refs, floatingStyles, placement } = useFloating({
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${ rects.reference.width }px`,
          });
        },
      }),
    ],
  });

  const isValueTheSameAsInitial = (value) => value?.replace(/[^+\d]/g, '') === meta.initial?.value?.replace(/[^+\d]/g, '');

  const getValue = () => {
    if (input && 'value' in input) {
      return input.value;
    }
    return value;
  };

  const applyCountriesPriority = (countries) => {
    return countries.map((country) => {
      return country.dialCode === '44' ?
        { ...country, priority: country.iso2 === 'gb' ? 0 : 1 } : country;
    });
  };

  /* TODO: @vladimirlomonos please refactor this code. */
  const updateFormattedNumber = () => {
    let changeValue = isChanged.current ? innerValue : innerValue || getValue();

    let val = isObject(changeValue) ? (changeValue as any).value : changeValue;
    const phoneInput = phoneInputRef.current;
    if (!phoneInput) return;

    const selectedCountry = (val && startsWith(val, prefix))
      ? phoneInput.guessSelectedCountry(
        val.replace(/\D/g, '').substring(0, 6),
        phoneInput.state.country,
        phoneInput.state.onlyCountries,
        phoneInput.state.hiddenAreaCodes,
      )
      : null;
    const dialCode = countryData.current?.dialCode ||
      selectedCountry?.dialCode ||
      phoneInput.getCountryData().dialCode ||
      defaultCountryData.current?.dialCode;

    if (isString(val) && !startsWith(val, prefix) && !startsWith(val, dialCode)) {
      phoneInput.setState({
        formattedNumber: `${ prefix }${ isChanged.current ? val : val || dialCode }`,
        country: defaultCountry,
        selectedCountry: defaultCountryData.current,
      });
    } else if (val) {
      val = val.replace(/\D/g, '');
      let newSelectedCountry = val.startsWith(phoneInput.state.selectedCountry?.dialCode)
        ? phoneInput.state.selectedCountry
        : phoneInput.guessSelectedCountry(val.substring(0, 6), defaultCountry, [], []);

      if (!newSelectedCountry) {
        newSelectedCountry = phoneInput.state.selectedCountry;
      }
      val = newSelectedCountry ? phoneInput.formatNumber(val, newSelectedCountry) : val;
      phoneInput.setState({ formattedNumber: val, selectedCountry: newSelectedCountry });
    }

    const finalValue = isValueTheSameAsInitial(val) ? meta.initial : {};
    finalValue.value = val === prefix ? '' : val;
    finalValue.code = prefix + dialCode;

    if (isFinalForm) {
      if (isEqual(finalValue, getValue())) return;
      input.onChange(finalValue);
      return;
    }

    if (input && 'onChange' in input) {
      input.onChange(finalValue);
      return;
    }

    onChange({ target: { value: finalValue } });
  };

  /**
   * @param {SyntheticEvent|string} event
   * @param {object} data
   * @return {String}
   */
  const handleOnChange = (event, data) => {
    const value = ((event instanceof Object) ? event.target.value : event);
    const { dialCode } = data;

    setInnerValue((value && startsWith(value.replace(prefix, ''), dialCode))
      ? phoneInputRef.current?.state?.formattedNumber
      : value);
    countryData.current = data;
    isChanged.current = true;
  };

  const onFocusInner = () => {
    setIsFocused(true);

    if (input && 'onFocus' in input) {
      return input.onFocus();
    }
    if (nodeInput.current) {
      nodeInput.current.focus();
    }

    return onFocus();
  };

  const handleOnBlur = () => {
    if (!isHovered) {
      setIsFocused(false);
    }

    if (input && 'onBlur' in input) {
      return input.onBlur();
    }
    return onBlur();
  };

  const handleHoverOn = () => {
    setIsHovered(true);
  };

  const handleHoverOff = () => {
    setIsHovered(false);
  };

  const onClearButtonClick = useCallback(() => {
    setInnerValue(phoneInputRef.current.getCountryData().dialCode);
  }, []);

  useEffect(() => {
    const overrideClickOutsideHandler = () => {
      if (!phoneInputRef.current) {
        return;
      }

      phoneInputRef.current.handleClickOutside = ((nativeHandleClickOutside) => {
        document.removeEventListener('mousedown', nativeHandleClickOutside);

        return (e) => {
          if (e?.target?.parentElement?.className !== 'country') {
            nativeHandleClickOutside(e);
          }
        };
      })(phoneInputRef.current.handleClickOutside);

      document.addEventListener('mousedown', phoneInputRef.current.handleClickOutside);
    };

    const overridePopperStyleCountryDropdownList = () => {
      if (!phoneInputRef.current) {
        return;
      }

      phoneInputRef.current.getCountryDropdownList = ((nativeCountryDropdownList) => () => (
        <PortalWrp
          usePortal={ usePortal }
          portalId={ portalId }
        >
          <div
            ref={ refs.setFloating }
            style={ usePortal ? {
              position: floatingStyles.position,
              top: floatingStyles.top,
              left: floatingStyles.left,
            } : undefined }
            data-placement={ placement }
            className={ styles.phoneDropdownWrp }
          >
            { nativeCountryDropdownList() }
          </div>
        </PortalWrp>
      ))(phoneInputRef.current.getCountryDropdownList);
    };

    defaultCountryData.current = phoneInputRef.current?.state?.onlyCountries
      .find((country) => country.iso2 === defaultCountry);
    overridePopperStyleCountryDropdownList();
    overrideClickOutsideHandler();

    if (phoneInputRef.current?.state?.onlyCountries) {
      const { onlyCountries } = phoneInputRef.current.state;
      const mergedCountries = [...onlyCountries, ...additionalCountries];
      const prioritisedCountries = applyCountriesPriority(mergedCountries);

      prioritisedCountries.sort((a, b) => a.name.localeCompare(b.name));
      phoneInputRef.current.state.onlyCountries = prioritisedCountries;
    }

    return () => {
      if (phoneInputRef.current) {
        document.removeEventListener('mousedown', phoneInputRef.current.handleClickOutside);
      }
    };
  }, []);

  useEffect(() => {
    updateFormattedNumber();
  });

  return (
    <FormField
      isNarrow={ isNarrow }
      isValidationFeedback={ !!(meta && meta.touched && meta.error) }
      dataTest={ dataTest }
      className={ formFieldClassName }
    >
      { label && (
        <Label htmlFor={ input.name }>
          { label }
        </Label>
      ) }
      <div
        ref={ refs.setReference }
        className={ styles.phoneWrp }
        onMouseOver={ handleHoverOn }
        onMouseLeave={ handleHoverOff }
        data-test="phoneInput"
      >
        <PhoneInput
          enableTerritories
          ref={ phoneInputRef }
          tabIndex="-1"
          disabled={ disabled }
          inputProps={ {
            name: input.name,
          } }
          containerClass={ cn(styles.phoneContainer, { [styles.small]: isSmall }) }
          inputClass={ cn(styles.phoneInput, inputStyles.formInput, { [inputStyles.sizeS]: isSmall }) }
          buttonClass={ styles.phoneButton }
          dropdownClass={ styles.phoneDropdown }
          country={ defaultCountry }
          placeholder={ placeholder }
          jumpCursorToEnd={ false }
          onChange={ handleOnChange }
          onBlur={ handleOnBlur }
          onFocus={ onFocusInner }
          value={ innerValue }
          copyNumbersOnly={ false }
          prefix={ prefix }
          specialLabel=""
          enableClickOutside={ false }
          isValid={ () => meta.valid || !meta.touched }
          localization={ { Turkey: 'Türkiye' } }
          dropdownStyle={ floatingStyles }
        />
        { isClearable && (
          <ClearButton
            isSmall={ isSmall }
            visible={
              !disabled && (isHovered || isFocused) && (innerValue?.length > 4 || input.value?.value?.length > 4)
            }
            clear={ onClearButtonClick }
          />
        ) }
      </div>
      { isErrorMessage && <FormError { ...meta } /> }
    </FormField>
  );
};

export default Phone;
